// Cross-Module Event Router - event routing logic extracted from CrossModuleIntegration
// ✅ NO MAGIC STRINGS: Use typed enums and constants

import { IntegrationEvents } from "../constants/Events";
import debugLogger from "../utils/debugLogger";

import {
  EventRouterState,
  CrossModuleEventQueue,
} from "./cross-module-event-queue";
import {
  CrossModuleEvent,
  ModuleInteraction,
  EventRoutingPattern,
  IntegrationError,
  IntegrationErrorType,
  CrossModulePayload,
} from "./types";
import {
  ModuleRegisteredPayload,
  ModuleUnregisteredPayload,
  CrossModuleErrorPayload,
  StateSyncResponsePayload,
  PerformanceMetricPayload,
  HealthCheckPayload,
} from "./types/EventPayloads";

/**
 * Event router: handles emission, handler registration, routing, and global
 * event handler setup. Queue processing and retry logic are delegated to
 * {@link CrossModuleEventQueue}. Mutates the shared state passed in.
 */
export class CrossModuleEventRouter {
  private eventQueue: CrossModuleEventQueue;

  constructor(private state: EventRouterState) {
    this.eventQueue = new CrossModuleEventQueue(state);
  }

  /**
   * Register a cross-module event handler with advanced routing
   */
  public registerCrossModuleHandler(
    sourceModule: string,
    targetModule: string,
    eventType: CrossModuleEvent,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handler: (payload: any) => Promise<void>,
    routingPattern?: EventRoutingPattern,
  ): string {
    const { state } = this;
    const interactionId = `${sourceModule}_${targetModule}_${eventType}`;

    const interaction: ModuleInteraction = {
      id: interactionId,
      sourceModule,
      targetModule,
      eventType,
      handler,
      registeredAt: new Date(),
      callCount: 0,
      lastCalled: null,
      successCount: 0,
      errorCount: 0,
      routingPattern,
      averageLatency: 0,
      lastLatency: 0,
    };

    if (!state.moduleInteractions.has(interactionId)) {
      state.moduleInteractions.set(interactionId, []);
    }
    state.moduleInteractions.get(interactionId)!.push(interaction);

    // Store routing pattern if provided
    if (routingPattern) {
      state.eventRoutingPatterns.set(interactionId, routingPattern);
    }

    // Subscribe to the global event
    const subscriptionId = state.eventBus.subscribeGlobal(
      `${sourceModule}:${eventType}`,
      async (payload: CrossModulePayload) => {
        await this.handleCrossModuleEvent(interaction, payload);
      },
    );

    debugLogger.info(
      "integration",
      `Cross-module handler registered with advanced routing: ${interactionId}`,
    );
    return subscriptionId;
  }

  /**
   * Emit a cross-module event with advanced routing and queuing
   */
  public async emitCrossModuleEvent(
    sourceModule: string,
    targetModule: string,
    eventType: CrossModuleEvent,
    payload: CrossModulePayload,
    priority: number = 1,
  ): Promise<void> {
    const { state } = this;
    const startTime = Date.now();

    try {
      state.metrics.totalCrossModuleEvents++;

      // Add to event queue for processing with priority
      this.eventQueue.addToEventQueue(
        `${sourceModule}:${eventType}`,
        {
          ...payload,
          sourceModule,
          targetModule,
          timestamp: new Date().toISOString(),
          eventId: this.generateEventId(),
        },
        priority,
      );

      // Track the interaction
      this.trackModuleInteraction(sourceModule, targetModule, eventType);

      state.metrics.successfulEvents++;
      state.metrics.averageEventLatency =
        this.calculateAverageLatency(startTime);

      debugLogger.info(
        "integration",
        `Cross-module event queued: ${sourceModule} -> ${targetModule}:${eventType}`,
      );
    } catch (error) {
      state.metrics.failedEvents++;
      const errorMessage = `Failed to emit cross-module event: ${sourceModule} -> ${targetModule}:${eventType}`;
      debugLogger.error("integration", errorMessage, error);
      throw new IntegrationError(
        IntegrationErrorType.EVENT_EMISSION_FAILED,
        errorMessage,
      );
    }
  }

  /**
   * Clear all cross-module handlers
   */
  public clearHandlers(): void {
    const { state } = this;
    state.moduleInteractions.clear();
    state.eventRoutingPatterns.clear();
    debugLogger.info("integration", "All cross-module handlers cleared");
  }

  /**
   * Set up global event handlers for module lifecycle and integration events.
   */
  public setupEventHandlers(): void {
    const { state } = this;

    // Handle module lifecycle events
    state.eventBus.subscribeGlobal(
      IntegrationEvents.MODULE_REGISTERED,
      (payload: ModuleRegisteredPayload) => {
        this.handleModuleRegistered(payload.name);
      },
    );

    state.eventBus.subscribeGlobal(
      IntegrationEvents.MODULE_UNREGISTERED,
      (payload: ModuleUnregisteredPayload) => {
        this.handleModuleUnregistered(payload.name);
      },
    );

    // Handle cross-module error events
    state.eventBus.subscribeGlobal(
      IntegrationEvents.CROSS_MODULE_ERROR,
      (payload: CrossModuleErrorPayload) => {
        this.handleCrossModuleError(payload);
      },
    );

    // Handle state sync responses
    state.eventBus.subscribeGlobal(
      IntegrationEvents.STATE_SYNC_RESPONSE,
      (payload: StateSyncResponsePayload) => {
        this.handleStateSyncResponse(payload);
      },
    );

    // Handle performance monitoring events
    state.eventBus.subscribeGlobal(
      IntegrationEvents.PERFORMANCE_METRIC,
      (payload: PerformanceMetricPayload) => {
        this.handlePerformanceMetric(payload);
      },
    );

    // Handle health check events
    state.eventBus.subscribeGlobal(
      IntegrationEvents.HEALTH_CHECK,
      (payload: HealthCheckPayload) => {
        this.handleHealthCheck(payload);
      },
    );
  }

  /**
   * Initialize advanced routing patterns.
   */
  public setupAdvancedRouting(): void {
    this.initializeRoutingPatterns();
  }

  private async handleCrossModuleEvent(
    interaction: ModuleInteraction,
    payload: CrossModulePayload,
  ): Promise<void> {
    const { state } = this;
    const startTime = Date.now();

    try {
      interaction.callCount++;
      interaction.lastCalled = new Date();

      // Apply routing pattern if exists
      const routingPattern = state.eventRoutingPatterns.get(interaction.id);
      if (routingPattern && state.config.enableAdvancedRouting) {
        await this.applyRoutingPattern(routingPattern, interaction, payload);
        state.metrics.routingPatternMatches++;
      } else {
        // Direct execution
        await interaction.handler(payload);
      }

      // Update performance metrics
      const latency = Date.now() - startTime;
      interaction.lastLatency = latency;
      interaction.averageLatency = this.calculateAverageLatency(latency);

      interaction.successCount++;
      state.metrics.moduleInteractions++;

      // Notify facade so performance metrics are updated
      if (state.onInteractionComplete) {
        state.onInteractionComplete(interaction.sourceModule, latency, true);
      }

      debugLogger.debug(
        "integration",
        `Cross-module event handled: ${interaction.id}`,
      );
    } catch (error) {
      interaction.errorCount++;
      const errorMessage = `Failed to handle cross-module event: ${interaction.id}`;
      debugLogger.error("integration", errorMessage, error);

      // Notify facade so performance metrics are updated
      const latency = Date.now() - startTime;
      if (state.onInteractionComplete) {
        state.onInteractionComplete(interaction.sourceModule, latency, false);
      }

      // Handle retry logic
      if (state.config.enableEventRetry) {
        await this.eventQueue.handleEventRetry(interaction, payload, error);
      }

      // Emit error event
      state.eventBus.emitGlobal(IntegrationEvents.CROSS_MODULE_ERROR, {
        interactionId: interaction.id,
        error: error instanceof Error ? error.message : String(error),
        payload,
      });
    }
  }

  private handleModuleRegistered(moduleName: string): void {
    if (this.state.onModuleRegistered) {
      this.state.onModuleRegistered(moduleName);
    }
    debugLogger.info(
      "integration",
      `Module registered for integration: ${moduleName}`,
    );
  }

  private handleModuleUnregistered(moduleName: string): void {
    if (this.state.onModuleUnregistered) {
      this.state.onModuleUnregistered(moduleName);
    }
    debugLogger.info(
      "integration",
      `Module unregistered from integration: ${moduleName}`,
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleCrossModuleError(payload: any): void {
    debugLogger.error("integration", "Cross-module error occurred", payload);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleStateSyncResponse(payload: any): void {
    // Handle state sync response
    debugLogger.debug("integration", "State sync response received", payload);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handlePerformanceMetric(payload: any): void {
    // Handle performance metric
    debugLogger.debug("integration", "Performance metric received", payload);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleHealthCheck(payload: any): void {
    // Handle health check
    debugLogger.debug("integration", "Health check received", payload);
  }

  private trackModuleInteraction(
    _sourceModule: string,
    _targetModule: string,
    _eventType: CrossModuleEvent,
  ): void {
    this.state.metrics.moduleInteractions++;
  }

  private calculateAverageLatency(startTime: number): number {
    const { state } = this;
    const latency = Date.now() - startTime;
    return (state.metrics.averageEventLatency + latency) / 2;
  }

  private generateEventId(): string {
    return `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private async applyRoutingPattern(
    _pattern: EventRoutingPattern,
    interaction: ModuleInteraction,
    payload: CrossModulePayload,
  ): Promise<void> {
    // Apply routing pattern logic
    await interaction.handler(payload);
  }

  private initializeRoutingPatterns(): void {
    // Initialize default routing patterns
    debugLogger.info("integration", "Advanced routing patterns initialized");
  }
}
